import { Component, effect, inject, signal } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TopbarComponent } from './topbar/topbar.component';
import { SidebarComponent } from './sidebar/sidebar.component';
import { AuthService } from '../../core/auth/auth.service';
import { ThemeService } from '../../core/services/theme.service';
import { TenantService } from '../../core/services/tenant.service';
import { PermissionService } from '../../core/services/permission.service';
import { ModuleService } from '../../core/services/module.service';

const SIDEBAR_COLLAPSED_KEY = 'compleet_sidebar_collapsed';

/**
 * App Shell: Topbar fixa + Sidebar recolhível + área de conteúdo
 * (router-outlet). Substitui o antigo shell plano (side-navigation +
 * top-toolbar).
 *
 * Dono do estado de recolhimento da sidebar (persistido em localStorage) e
 * da orquestração de recarregamento de PermissionService/ModuleService
 * sempre que o tenant ativo muda — regra central do App Shell: o menu é
 * dinâmico por módulo habilitado + permissão do usuário (PBAC), nunca
 * status hardcoded no template.
 */
@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [RouterOutlet, TopbarComponent, SidebarComponent],
  templateUrl: './app-shell.component.html',
  styleUrl: './app-shell.component.scss',
})
export class AppShellComponent {
  readonly authService = inject(AuthService);
  readonly themeService = inject(ThemeService);
  readonly tenantService = inject(TenantService);
  private readonly permissionService = inject(PermissionService);
  private readonly moduleService = inject(ModuleService);

  readonly sidebarCollapsed = signal(this.resolveInitialCollapsed());
  readonly mobileDrawerOpen = signal(false);

  constructor() {
    // Recarrega permissões e módulos sempre que o tenant ativo muda —
    // inclui o carregamento inicial (login) e toda troca de empresa.
    effect(() => {
      const tenantId = this.tenantService.activeTenantId();
      if (tenantId) {
        this.permissionService.load().subscribe();
        this.moduleService.load().subscribe();
      } else {
        this.permissionService.clear();
        this.moduleService.clear();
      }
    });
  }

  toggleSidebar(): void {
    this.sidebarCollapsed.update(v => {
      const next = !v;
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      return next;
    });
  }

  openMobileDrawer(): void {
    this.mobileDrawerOpen.set(true);
  }

  closeMobileDrawer(): void {
    this.mobileDrawerOpen.set(false);
  }

  private resolveInitialCollapsed(): boolean {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  }
}
